import React from 'react';
import { ProjectedShade } from '../components/fabric/ProjectedShade';

/* Visual test page: renders the same room image 4 times with fabric overlays at 1%, 3%, 5%, 10%
   to verify per-quad openness compositing. The shade quad covers the window area only.
   This simulates FULL compositing mode (clean bg + fabric overlay on detected quads). */

const SAMPLE_ROOM = 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80';
const FABRIC_HEX = '#3E3438'; // Charcoal Dark Bronze approximation

/* Sample shade quad covering a typical window area (normalized 0-1 coords) */
const SHADE_QUAD = [
  [0.18, 0.08],  // top-left
  [0.82, 0.08],  // top-right
  [0.82, 0.88],  // bottom-right
  [0.18, 0.88],  // bottom-left
];

const OPENNESS_LEVELS = [
  { pct: 1, opacity: 0.98, label: '1% — Nearly opaque' },
  { pct: 3, opacity: 0.94, label: '3% — Mostly opaque' },
  { pct: 5, opacity: 0.88, label: '5% — Semi-transparent' },
  { pct: 10, opacity: 0.76, label: '10% — Most transparent' },
];

const SceneWithOverlay = ({ level, containerSize }) => (
  <div className="relative overflow-hidden rounded-lg border border-neutral-700" style={{ width: containerSize, height: containerSize * 0.75 }}>
    <img src={SAMPLE_ROOM} alt="Room" className="w-full h-full object-cover" crossOrigin="anonymous" />
    <div className="absolute inset-0 pointer-events-none" data-testid={`test-openness-${level.pct}`}>
      <ProjectedShade
        quad={SHADE_QUAD}
        cw={containerSize}
        ch={containerSize * 0.75}
        hexColor={FABRIC_HEX}
        opacity={level.opacity}
        mode="screen"
        ambience="day"
        testId={`test-shade-${level.pct}`}
      />
    </div>
    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-center text-sm py-1.5 font-medium">
      {level.label}
      <span className="text-xs text-neutral-400 ml-2">opacity: {level.opacity.toFixed(2)}</span>
    </div>
  </div>
);

export default function OpennessTestPage() {
  const containerSize = 420;

  return (
    <div className="min-h-screen bg-neutral-900 text-white p-8">
      <h1 className="text-2xl font-bold mb-2">Openness Compositing Test</h1>
      <p className="text-neutral-400 mb-6 max-w-2xl">
        Each panel shows the same room image with a shade fabric overlay applied <strong>only on the window quad</strong> (not the whole room).
        Room walls, floor, and furniture remain untouched. This simulates full compositing (clean background + fabric overlay on detected shade quads).
      </p>

      <div className="grid grid-cols-2 gap-4 max-w-[880px]">
        {OPENNESS_LEVELS.map((level) => (
          <SceneWithOverlay key={level.pct} level={level} containerSize={containerSize} />
        ))}
      </div>

      <div className="mt-8 p-4 bg-neutral-800 rounded-lg max-w-2xl text-sm text-neutral-300">
        <h2 className="font-semibold text-white mb-2">How it works</h2>
        <ul className="space-y-1 list-disc list-inside">
          <li><strong>Full compositing</strong>: Base image is the clean background (no shade). Fabric overlay at OPENNESS_OPACITY values creates the shade on detected window quads.</li>
          <li>Only the shade quad area is affected — room furniture/walls stay untouched.</li>
          <li>1% = darkest/most opaque → 10% = most transparent/clearest exterior view.</li>
          <li>Requires both <code>clean_url</code> + <code>polygons</code> from shade_regions detection.</li>
        </ul>
      </div>
    </div>
  );
}
